import { useState, type JSX } from 'react';
import {
  appointmentListSchema,
  generateUuidV7,
  type BotScope,
} from '@cryptology-assist/domain';
import { botPath, idSchema, request, type Session } from './api';
import { useRemote } from './hooks';
import { ErrorNotice, Field, onSubmit, useMutation } from './components';
import { dateLabel, type Copy, type Language } from './copy';

export function Appointments({
  scope,
  conversationId,
  session,
  copy,
  language,
  canWrite,
  onChanged,
}: {
  scope: BotScope;
  conversationId: string;
  session: Session;
  copy: Copy;
  language: Language;
  canWrite: boolean;
  onChanged: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false),
    [before, setBefore] = useState<string | null>(null);
  const path = botPath(scope, `conversations/${conversationId}`);
  const appointments = useRemote(
    open
      ? `${path}/appointments${before ? `?before=${encodeURIComponent(before)}` : ''}`
      : null,
    appointmentListSchema
  );
  return (
    <section>
      <button
        className="quiet"
        onClick={() => {
          setOpen(!open);
        }}
      >
        {copy.appointments}
      </button>
      {open && (
        <div>
          <button className="quiet" onClick={appointments.reload}>
            {copy.refresh}
          </button>
          <ErrorNotice error={appointments.error} copy={copy} />
          {!appointments.data && !appointments.error && <p>{copy.loading}</p>}
          {appointments.data?.appointments.length === 0 && (
            <p>{copy.noAppointments}</p>
          )}
          {appointments.data?.appointments.map((appointment) => (
            <AppointmentChange
              key={`${appointment.actionId}:${appointment.booking?.version ?? 'cancelled'}`}
              appointment={appointment}
              path={path}
              session={session}
              copy={copy}
              language={language}
              canWrite={canWrite}
              onChanged={() => {
                appointments.reload();
                onChanged();
              }}
            />
          ))}
          {before && (
            <button className="quiet" onClick={() => setBefore(null)}>
              {copy.newest}
            </button>
          )}
          {appointments.data?.nextCursor && (
            <button
              className="quiet"
              onClick={() => setBefore(appointments.data!.nextCursor)}
            >
              {copy.older}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function AppointmentChange({
  appointment,
  path,
  session,
  copy,
  language,
  canWrite,
  onChanged,
}: {
  appointment: ReturnType<
    typeof appointmentListSchema.parse
  >['appointments'][number];
  path: string;
  session: Session;
  copy: Copy;
  language: Language;
  canWrite: boolean;
  onChanged: () => void;
}): JSX.Element {
  const [kind, setKind] = useState<
      'cancel_appointment' | 'reschedule_appointment'
    >('reschedule_appointment'),
    [reason, setReason] = useState(''),
    [startsAt, setStartsAt] = useState(''),
    [endsAt, setEndsAt] = useState(''),
    [confirmed, setConfirmed] = useState(false),
    [submitted, setSubmitted] = useState(false);
  const mutation = useMutation();
  const booking = appointment.booking;
  return (
    <article className="action-summary">
      <strong>{appointment.subject}</strong>
      {booking ? (
        <p>
          {dateLabel(booking.startsAt, language)} —{' '}
          {dateLabel(booking.endsAt, language)}
        </p>
      ) : (
        <p>{copy.appointmentCancelled}</p>
      )}
      {booking && canWrite && !submitted && (
        <form
          onSubmit={(event) =>
            onSubmit(event, () =>
              mutation.run(async () => {
                if (!confirmed) return;
                const action =
                  kind === 'cancel_appointment'
                    ? {
                        kind,
                        appointmentActionId: appointment.actionId,
                        expectedVersion: booking.version,
                        reason,
                      }
                    : {
                        kind,
                        appointmentActionId: appointment.actionId,
                        expectedVersion: booking.version,
                        startsAt: new Date(startsAt).toISOString(),
                        endsAt: new Date(endsAt).toISOString(),
                      };
                await request(`${path}/appointment-changes`, idSchema, {
                  csrf: session.csrf,
                  body: { id: generateUuidV7(), action },
                });
                setSubmitted(true);
                onChanged();
              })
            )
          }
        >
          <Field label={copy.appointmentChange}>
            <select
              value={kind}
              onChange={(event) => {
                setKind(
                  event.target.value === 'cancel_appointment'
                    ? 'cancel_appointment'
                    : 'reschedule_appointment'
                );
                setConfirmed(false);
              }}
            >
              <option value="reschedule_appointment">
                {copy.rescheduleAppointment}
              </option>
              <option value="cancel_appointment">
                {copy.cancelAppointment}
              </option>
            </select>
          </Field>
          {kind === 'cancel_appointment' ? (
            <Field label={copy.reason}>
              <textarea
                required
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                  setConfirmed(false);
                }}
              />
            </Field>
          ) : (
            <>
              <p>{copy.appointmentLocalTime}</p>
              <Field label={copy.appointmentStarts}>
                <input
                  required
                  type="datetime-local"
                  value={startsAt}
                  onChange={(event) => {
                    setStartsAt(event.target.value);
                    setConfirmed(false);
                  }}
                />
              </Field>
              <Field label={copy.appointmentEnds}>
                <input
                  required
                  type="datetime-local"
                  value={endsAt}
                  onChange={(event) => {
                    setEndsAt(event.target.value);
                    setConfirmed(false);
                  }}
                />
              </Field>
            </>
          )}
          <label>
            <input
              type="checkbox"
              required
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            {copy.confirmAppointmentChange}
          </label>
          <button type="submit" disabled={!confirmed || mutation.busy}>
            {copy.submitAppointmentChange}
          </button>
        </form>
      )}
      {submitted && <p role="status">{copy.appointmentChangeRequested}</p>}
      <ErrorNotice error={mutation.error} copy={copy} />
    </article>
  );
}
